import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../context/themeContext';
import TemplateTable from '../../shared/tables/TemplateTable';
import { ITableHeader } from '../../shared/tables/models/ITableHeader';
import LoaderContainer from '../../shared/loaderContainer/LoaderContainer';
import { HTTP_STATUS } from '../../../http/constants/http.status';
import moment from 'moment';
import { AppConstants } from '../../../constants/AppConstants';
import BiButton from '../../primitives/buttons/BiButton.primitive';
import { display } from '../../../assets/components/svgs';
import { getQuestionnaires } from '../../../http/survey-service';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useDidMountEffect } from '../../../util/customHooks';
import { setToastData } from '../../../app/toastReducer';
import { getFailureMessage } from '../../../util/ErrorUtil';
import { setTableOptions } from '../../../app/templateTableReducer';

const Container = styled.div`
  width: 100%;
  padding-bottom: 20px;
  min-height: 25vh;
  max-height: 85vh;
  overflow: auto;
`;

const CustomersTableContainer = styled.div`
  padding: 1rem;
  width: 100%;
`;

interface SurveyVersioningProps {
  currentQuestionnaire: any;
  triggerVersion: Function;
  closeModal: Function;
}

const SurveyVersioning: React.FC<SurveyVersioningProps> = ({
  currentQuestionnaire,
  triggerVersion,
  closeModal
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [versions, setVersions] = useState<any[] | string>([]);
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState<any>(null);

  const globalFilters: any = useAppSelector((state) => state.templateTableReducer.tableOptions);
  const surveyListState: string = `survey-versioning-list`;

  useDidMountEffect(() => {
    if (globalFilters[surveyListState]) {
      const { tableFilters, searchText, currentPage, size, sortName, sortType } =
        globalFilters[surveyListState];
      applyFilters(searchText, currentPage, size, sortName, sortType, tableFilters);
    }
  }, [globalFilters]);

  const getSurveyList = async ({ ...options }) => {
    const { page, size, search = '', sortOrder = '', sortType = '' } = options;
    try {
      const versionData = await getQuestionnaires(
        page,
        size,
        sortType ? [`${sortOrder},${sortType}`] : [],
        search,
        {
          parentId: [
            {
              id: currentQuestionnaire?.parentId
            }
          ]
        }
      );
      const { status, data } = versionData;
      if (status == HTTP_STATUS.HTTP_OK) {
        const { data: surveyList = [], paging } = data;

        if (surveyList.length) {
          setVersions(surveyList);
          setPaging(paging);
        } else {
          setVersions(`${t('T_NO_DATA_AVAILABLE_MSG')}`);
        }
      } else {
        dispatch(
          setToastData({
            toastMessage: getFailureMessage(versionData),
            isToastActive: true,
            toastType: 'error'
          })
        );
        closeModal();
      }
    } catch (error) {
      closeModal();
      dispatch(
        setToastData({
          toastMessage: t('T_ERROR_SOMETHING_WRONG'),
          isToastActive: true,
          toastType: 'error'
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async (
    search: string = '',
    page: number,
    size: number,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    const appliedFilterList = Object.keys(filters);
    getSurveyList({ page, size, search, sortOrder, sortType, filters });
  };

  const TABLE_HEADERS: Array<ITableHeader> = [
    {
      key: 'questionnaireVersion',
      description: 'T_VERSION',
      sortEnabled: true,
      width: '10%',
      sortingInformation: {
        order: 'desc'
      },
      render: (data: any) => {
        return data.questionnaireVersion ? (
          <div className="flex w-full items-center justify-center">{data.questionnaireVersion}</div>
        ) : (
          ''
        );
      }
    },
    {
      key: 'name',
      description: 'T_NAME',
      sortEnabled: true,
      width: '25%',
      sortingInformation: {
        order: ''
      },
      nastedData: false,
      render: (data: any) => {
        return data.name ? <div className="max-w-full truncate">{data.name}</div> : '';
      }
    },
    {
      key: 'createdAt',
      description: t('T_CREATED_ON'),
      sortEnabled: true,
      width: '25%',
      sortingInformation: {
        order: ''
      },
      nastedData: false,
      render: (data: any) => {
        return data.createdOn
          ? moment(data.createdAt).format(AppConstants.DEFAULT_DATE_FORMAT)
          : '';
      }
    },
    {
      key: 'updatedOn',
      description: t('T_LAST_MODIFIED_ON'),
      sortEnabled: true,
      width: '25%',
      sortingInformation: {
        order: ''
      },
      nastedData: false,
      render: (data: any) => {
        return data.updatedOn
          ? moment(data.updatedOn).format(AppConstants.DEFAULT_DATE_FORMAT)
          : '';
      }
    },
    {
      key: 'options',
      description: '',
      sortEnabled: false,
      width: '15%',
      sortingInformation: {
        order: ''
      },
      clickable: false,
      nastedData: true,
      render: (data: any) => {
        return (
          <BiButton
            className={'!ml-0 mt-2 !px-0 !py-0 !text-start text-primary'}
            type="button"
            onClick={() => {
              triggerVersion(data);
            }}
          >
            {t('T_VIEW_DETAILS')}
          </BiButton>
        );
      },
      styles: {
        paddingRight: '0',
        paddingLeft: '0',
        borderLeft: 'none',
        rowStyle: {
          justifyContent: 'center',
          display: 'flex',
          textAlign: 'center'
        }
      }
    }
  ];
  const [tableHeaders, setTableHeaders] = useState<Array<ITableHeader>>(TABLE_HEADERS);

  const refreshTableData = (
    search: string = '',
    page: number,
    size: number,
    sortOrder: string = '',
    sortType: string = '',
    filters: any
  ) => {
    setLoading(true);
  };

  useEffect(() => {
    const init = async () => {
      const {
        tableFilters = {},
        searchText = '',
        currentPage = AppConstants.DEFAULT_PAGE,
        size = AppConstants.DEFAULT_PAGE_SIZE,
        sortName = '',
        sortType = ''
      } = globalFilters[surveyListState] || {};

      dispatch(
        setTableOptions({
          [surveyListState]: {
            searchText: searchText,
            currentPage,
            size,
            sortName: sortName,
            sortType: sortType,
            tableFilters: tableFilters
          }
        })
      );
    };
    init();
  }, []);

  return (
    <LoaderContainer isLoading={loading}>
      <Container>
        <CustomersTableContainer>
          <TemplateTable
            isLoading={loading}
            tableData={versions}
            totalDataLength={versions.length}
            searchPlaceholder={t('T_SEARCH_NAME_SURVEY_TEMPLATE') || ''}
            currentPage={globalFilters?.[surveyListState]?.currentPage}
            templateSelector={() => {}}
            onRefreshTableData={refreshTableData}
            tableHeaders={tableHeaders}
            setTableHeaders={setTableHeaders}
            containerClass="mt-0"
            isSearchable
            isFilterable={false}
            tableName={t('T_SURVEY_VERSIONS')}
            pagingData={paging}
            size={globalFilters?.[surveyListState]?.size}
            globalFilters={globalFilters}
            theme={useTheme().theme}
            type={'listing'}
            tablePage={surveyListState}
            isLocalPaginator={false}
            isPaginated
          />
        </CustomersTableContainer>
      </Container>
    </LoaderContainer>
  );
};

export default SurveyVersioning;
